import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Bell, CalendarClock, Check, ChevronDown, ChevronRight, Loader2, Lock, Mail, RotateCcw, Save, ShieldAlert, Table2, X } from 'lucide-react';
import { api } from '@/api/client';
import { useAuth } from '@/hooks/useAuth';

interface Channel { key: string; label: string; hint: string; value: string }
interface MisColumn { key: string; label: string; group: string; sensitive?: boolean }
interface MisRecipient { email: string | null; ok: boolean; error: string | null; at: string }
/** One mailing: the whole recipient list for a single sheet, not one row per address. */
interface MisSend {
  startedAt: string;
  sentAt: string;
  subject: string | null;
  fileName: string | null;
  rows: number;
  sentBy: string;
  scheduled: boolean;
  sent: number;
  failed: number;
  recipients: MisRecipient[];
}
interface MisHistory { days: number; since: string; sends: MisSend[]; emails: number; truncated: boolean }
interface Mis {
  enabled: boolean;
  /** null = this merchant has never been customised, so the platform default set applies. */
  columns: string[] | null;
  defaultColumns: string[];
  catalogue: MisColumn[];
  lastSentAt: string | null;
  lastStatus: string | null;
  lastRowCount: number | null;
  lastError: string | null;
  history?: MisHistory;
  historyDays?: number;
}
interface Payload {
  merchantId: number;
  accountEmail: string;
  channels: Channel[];
  /** Absent for a merchant caller — the API does not disclose the MIS selection to them. */
  mis?: Mis;
  updatedAt: string | null;
}

/**
 * The last week of MIS mailings for this merchant.
 *
 * Grouped by mailing, not by address. One MIS goes to every address on the list as its own email —
 * so a single morning's send is a dozen log rows, and listing them flat would read as a dozen
 * separate mailings and bury the question somebody opened this to answer: did today's sheet go, and
 * did every address on the list get it. Each row expands to the addresses when that is the question.
 *
 * A mailing where some addresses failed is called out in amber rather than being folded into a
 * "sent" tick — a bounce on one address is exactly the case where nobody notices for a month.
 */
function MisHistoryBlock({ history }: { history: MisHistory }) {
  const [open, setOpen] = useState<string | null>(null);

  const when = (iso: string) =>
    new Date(iso).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' });

  if (!history.sends.length) {
    return (
      <div className="px-3 py-3 text-xs text-slate-400 border-t border-slate-100">
        No MIS emails sent to this merchant in the last {history.days} days.
      </div>
    );
  }

  return (
    <div className="border-t border-slate-100">
      <div className="px-3 pt-2.5 pb-1.5 flex flex-wrap items-baseline gap-2">
        <span className="text-xs font-semibold text-slate-700 flex items-center gap-1.5">
          <CalendarClock size={13} className="text-slate-400" /> Sent in the last {history.days} days
        </span>
        <span className="text-xs text-slate-400">
          {history.sends.length} mailing{history.sends.length === 1 ? '' : 's'} · {history.emails} email
          {history.emails === 1 ? '' : 's'}
        </span>
      </div>

      <div className="divide-y divide-slate-100">
        {history.sends.map((sd) => {
          const id = sd.sentAt + (sd.fileName ?? '');
          const isOpen = open === id;
          return (
            <div key={id}>
              <button
                type="button"
                onClick={() => setOpen(isOpen ? null : id)}
                className="w-full px-3 py-2 flex items-center gap-2 text-left hover:bg-slate-50"
              >
                {isOpen ? <ChevronDown size={13} className="text-slate-400 shrink-0" />
                        : <ChevronRight size={13} className="text-slate-400 shrink-0" />}
                <span className="text-xs font-medium text-slate-700 shrink-0">{when(sd.sentAt)}</span>
                <span
                  className={`text-[10px] px-1.5 py-px rounded shrink-0 ${
                    sd.scheduled
                      ? 'bg-indigo-50 text-indigo-700 border border-indigo-200'
                      : 'bg-slate-100 text-slate-600 border border-slate-200'
                  }`}
                >
                  {sd.scheduled ? 'scheduled' : sd.sentBy}
                </span>
                <span className="text-xs text-slate-500 truncate">
                  {sd.rows.toLocaleString('en-IN')} rows · {sd.sent + sd.failed} recipient
                  {sd.sent + sd.failed === 1 ? '' : 's'}
                </span>
                <span className="ml-auto shrink-0 text-xs">
                  {sd.failed === 0 ? (
                    <span className="text-emerald-600 flex items-center gap-1"><Check size={12} /> all delivered</span>
                  ) : (
                    <span className="text-amber-600 flex items-center gap-1">
                      <ShieldAlert size={12} /> {sd.failed} of {sd.sent + sd.failed} failed
                    </span>
                  )}
                </span>
              </button>

              {isOpen && (
                <div className="px-3 pb-2.5 pl-8 space-y-1">
                  {sd.subject && <div className="text-[11px] text-slate-500 truncate">Subject: {sd.subject}</div>}
                  {sd.fileName && <div className="text-[11px] text-slate-400 truncate">File: {sd.fileName}</div>}
                  <div className="grid sm:grid-cols-2 gap-x-4 gap-y-0.5 pt-1">
                    {sd.recipients.map((r, i) => (
                      <div key={`${r.email}-${i}`} className="flex items-start gap-1.5 text-[11px]">
                        {r.ok ? <Check size={11} className="text-emerald-500 shrink-0 mt-0.5" />
                              : <X size={11} className="text-rose-500 shrink-0 mt-0.5" />}
                        <span className={r.ok ? 'text-slate-600' : 'text-rose-600'}>
                          {r.email ?? '—'}
                          {!r.ok && r.error && <span className="text-slate-400"> — {r.error}</span>}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {history.truncated && (
        <div className="px-3 pb-2 text-[11px] text-slate-400">
          Showing the most recent 500 emails — see Master Data → Logs for the rest.
        </div>
      )}
    </div>
  );
}

/**
 * Per-merchant notification routing — one comma-separated address list per mail stream.
 *
 * ONE component for both sides: the merchant edits it under Settings, an admin edits the same thing
 * under Merchant Master. A second implementation would drift, and then the merchant and the admin
 * would be looking at different truths about where their invoices go.
 *
 * `merchantId` is supplied by the admin screen; the merchant portal omits it and hits its own
 * id-less route.
 *
 * The MIS block below is the one part that is NOT shared: see `internal`.
 */
export function NotificationEmailsPanel({ merchantId, readOnly }: { merchantId?: number; readOnly?: boolean }) {
  const qc = useQueryClient();
  const { user } = useAuth();
  const base = merchantId ? `/merchants/${merchantId}/notification-emails` : '/merchant/notification-emails';
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [misEnabled, setMisEnabled] = useState(false);
  const [misColumns, setMisColumns] = useState<string[] | null>(null);
  const [dirty, setDirty] = useState(false);

  /**
   * Internal staff only.
   *
   * The whole reason the picker exists is to keep certain columns AWAY from a given client — if the
   * merchant could open this and tick "Charged Weight" back on, the control would be decorative.
   * The API refuses these two fields from a merchant token as well; this only stops us showing a
   * merchant a switch they are not allowed to throw.
   */
  const internal = user?.kind !== 'merchant';

  const { data, isLoading } = useQuery<Payload>({
    queryKey: ['merchant-notification-emails', merchantId ?? 'me'],
    queryFn: async () => (await api.get(base)).data,
  });

  // Seed the boxes once the server answers, but never overwrite something half-typed.
  useEffect(() => {
    if (!data || dirty) return;
    setDraft(Object.fromEntries(data.channels.map((c) => [c.key, c.value ?? ''])));
    setMisEnabled(!!data.mis?.enabled);
    setMisColumns(data.mis?.columns ?? null);
  }, [data, dirty]);

  const save = useMutation({
    mutationFn: async () => {
      // `misColumns: []` is how "use the platform default set" is sent — the API turns an empty
      // array back into NULL. It is NOT "send an empty sheet", which is unsendable anyway.
      const body: any = { ...draft };
      if (internal) {
        body.misEnabled = misEnabled;
        body.misColumns = misColumns ?? [];
      }
      return (await api.put(base, body)).data;
    },
    onSuccess: () => {
      toast.success('Notification settings saved');
      setDirty(false);
      qc.invalidateQueries({ queryKey: ['merchant-notification-emails', merchantId ?? 'me'] });
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Could not save'),
  });

  if (isLoading) return <div className="py-10 text-center text-slate-400"><Loader2 size={22} className="animate-spin mx-auto" /></div>;

  const mis = data?.mis;
  const catalogue = mis?.catalogue ?? [];
  // No selection stored = the default set is what actually goes out, so that is what must be ticked.
  const selected = new Set(misColumns ?? mis?.defaultColumns ?? []);
  const usingDefaults = misColumns === null;
  const groups = Array.from(new Set(catalogue.map((c) => c.group)));

  const toggleCol = (key: string) => {
    if (readOnly) return;
    const next = new Set(selected);
    if (next.has(key)) next.delete(key); else next.add(key);
    // Keep catalogue order rather than click order — this list becomes the sheet's column order.
    setMisColumns(catalogue.filter((c) => next.has(c.key)).map((c) => c.key));
    setDirty(true);
  };

  /**
   * An empty selection is REFUSED rather than saved.
   *
   * The API reads `misColumns: []` as "this merchant has no custom set, use the platform default" —
   * which is what the Reset button relies on. So a save with everything unticked would hand the
   * merchant the FULL default sheet, including the columns just unticked to keep them away from it.
   * Blocking the save is the only reading that cannot surprise anyone: an empty sheet is not a
   * thing anyone wants sent, and silently-becomes-everything certainly is not.
   */
  const emptySelection = internal && !usingDefaults && selected.size === 0;

  const lastSent = mis?.lastSentAt
    ? new Date(mis.lastSentAt).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })
    : null;

  return (
    <div className="space-y-4">
      <div className="flex items-start gap-2 text-sm text-slate-600 bg-slate-50 border border-slate-200 rounded-lg p-3">
        <Bell size={16} className="shrink-0 mt-0.5 text-slate-400" />
        <div>
          Choose who receives each kind of email. Separate several addresses with commas.
          <div className="text-xs text-slate-500 mt-1">
            Leave a box empty and that mail goes to the account address,{' '}
            <b className="text-slate-700">{data?.accountEmail}</b>.
          </div>
        </div>
      </div>

      <div className="space-y-3">
        {(data?.channels ?? []).map((c) => (
          <div key={c.key}>
            <label className="flex items-baseline gap-2 mb-1">
              <span className="text-sm font-semibold text-slate-700">{c.label}</span>
              <span className="text-xs text-slate-400">{c.hint}</span>
            </label>
            <div className="relative">
              <Mail size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                value={draft[c.key] ?? ''}
                readOnly={readOnly}
                onChange={(e) => { setDraft({ ...draft, [c.key]: e.target.value }); setDirty(true); }}
                placeholder={`${data?.accountEmail ?? 'accounts@example.com'}`}
                className={`input w-full pl-8 ${readOnly ? 'bg-slate-50 text-slate-500' : ''}`}
              />
            </div>

            {/* The scheduled-MIS controls belong to the MIS Summary address, so they sit under it. */}
            {c.key === 'misSummary' && internal && mis && (
              <div className="mt-2 rounded-lg border border-slate-200 bg-white">
                <div className="flex flex-wrap items-center gap-3 px-3 py-2.5 border-b border-slate-100">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={misEnabled}
                      disabled={readOnly}
                      onChange={(e) => { setMisEnabled(e.target.checked); setDirty(true); }}
                      className="w-4 h-4 accent-primary"
                    />
                    <span className="text-sm font-semibold text-slate-700">Send the scheduled MIS to this merchant</span>
                  </label>
                  <span className="text-[11px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-500 flex items-center gap-1">
                    <Lock size={10} /> internal only — the merchant cannot change this
                  </span>
                  <div className="ml-auto text-xs text-slate-500">
                    {lastSent ? (
                      <>
                        Last sent <b className="text-slate-700">{lastSent}</b>
                        {mis.lastRowCount != null && <> · {mis.lastRowCount} rows</>}
                        {mis.lastStatus && mis.lastStatus !== 'SENT' && (
                          <span className="ml-1 text-amber-600">({mis.lastStatus})</span>
                        )}
                      </>
                    ) : (
                      <span className="text-slate-400">Never sent</span>
                    )}
                  </div>
                </div>

                {mis.lastError && (
                  <div className="px-3 py-2 text-xs text-rose-700 bg-rose-50 border-b border-rose-100 flex items-start gap-1.5">
                    <ShieldAlert size={13} className="shrink-0 mt-0.5" /> Last attempt failed: {mis.lastError}
                  </div>
                )}

                <div className="px-3 py-2.5">
                  <div className="flex flex-wrap items-center gap-2 mb-2">
                    <span className="text-xs font-semibold text-slate-700 flex items-center gap-1.5">
                      <Table2 size={13} className="text-slate-400" /> Columns in this merchant's sheet
                    </span>
                    <span className="text-xs text-slate-400">
                      {selected.size} of {catalogue.length}
                      {usingDefaults && ' · platform default'}
                    </span>
                    {!readOnly && !usingDefaults && (
                      <button
                        type="button"
                        onClick={() => { setMisColumns(null); setDirty(true); }}
                        className="text-xs text-slate-500 hover:text-primary flex items-center gap-1"
                      >
                        <RotateCcw size={11} /> Reset to default
                      </button>
                    )}
                  </div>

                  <p className="text-[11px] text-slate-500 mb-2.5">
                    Untick a column and it is left out of every sheet this merchant receives. Marked
                    columns are the ones normally withheld — a charged weight on a sheet we sent is
                    something a client can argue the freight over.
                  </p>

                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-3">
                    {groups.map((g) => (
                      <div key={g}>
                        <div className="text-[10px] font-bold uppercase tracking-wide text-slate-400 mb-1">{g}</div>
                        <div className="space-y-1">
                          {catalogue.filter((c) => c.group === g).map((col) => (
                            <label
                              key={col.key}
                              className={`flex items-start gap-1.5 text-xs ${readOnly ? '' : 'cursor-pointer'} ${
                                selected.has(col.key) ? 'text-slate-700' : 'text-slate-400'
                              }`}
                            >
                              <input
                                type="checkbox"
                                checked={selected.has(col.key)}
                                disabled={readOnly}
                                onChange={() => toggleCol(col.key)}
                                className="w-3.5 h-3.5 mt-0.5 accent-primary shrink-0"
                              />
                              <span>
                                {col.label}
                                {col.sensitive && (
                                  <span className="ml-1 text-[9px] px-1 py-px rounded bg-amber-50 text-amber-700 border border-amber-200 align-middle">
                                    sensitive
                                  </span>
                                )}
                              </span>
                            </label>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>

                  {emptySelection && (
                    <div className="mt-2 text-xs text-rose-600">
                      Pick at least one column. Leaving them all unticked cannot be saved — use
                      <b> Reset to default</b> if you meant the standard set.
                    </div>
                  )}
                </div>

                {mis.history && <MisHistoryBlock history={mis.history} />}
              </div>
            )}
          </div>
        ))}
      </div>

      {!readOnly && (
        <div className="flex items-center gap-3 pt-1">
          <button onClick={() => save.mutate()} disabled={save.isPending || !dirty || emptySelection}
            className="px-5 py-2 rounded-lg bg-primary text-white text-sm font-medium hover:bg-primary-700 disabled:opacity-50 flex items-center gap-2">
            {save.isPending ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Save
          </button>
          {dirty && <span className="text-xs text-amber-600">Unsaved changes</span>}
          {data?.updatedAt && !dirty && (
            <span className="text-xs text-slate-400">
              Last updated {new Date(data.updatedAt).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}
            </span>
          )}
        </div>
      )}
    </div>
  );
}
